import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { newsApi, formatApiError, resolveImageUrl } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Trash2, Upload, RefreshCw, Image, ExternalLink, Clock, AlertCircle, Pencil, ArrowUp, ArrowDown, GripVertical, Save } from 'lucide-react';

export default function AdminNewsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [error, setError] = useState('');
  const [scraping, setScraping] = useState(false);
  const [scrapeResult, setScrapeResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [orderChanged, setOrderChanged] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const [newArticle, setNewArticle] = useState({
    title: '',
    content: '',
    summary: '',
    source_url: '',
    source_name: '',
    tags: '',
  });

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchArticles();
  }, [user, navigate]);

  const fetchArticles = async () => {
    try {
      const res = await newsApi.adminList();
      setArticles(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const data = {
        title: newArticle.title,
        content: newArticle.content,
        summary: newArticle.summary || newArticle.content.slice(0, 200),
        source_url: newArticle.source_url || null,
        source_name: newArticle.source_name || 'Manual Entry',
        tags: newArticle.tags ? newArticle.tags.split(',').map(t => t.trim()) : ['crown vic', 'racing'],
      };
      await newsApi.create(data);
      setIsCreateOpen(false);
      setNewArticle({ title: '', content: '', summary: '', source_url: '', source_name: '', tags: '' });
      fetchArticles();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    try {
      await newsApi.delete(id);
      setArticles(articles.filter(a => a.id !== id));
    } catch (err) {
      alert(formatApiError(err));
    }
  };

  const openEdit = (article) => {
    setEditingArticle({
      id: article.id,
      title: article.title,
      content: article.content || '',
      summary: article.summary || '',
      source_url: article.source_url || '',
      source_name: article.source_name || '',
      tags: article.tags?.join(', ') || '',
    });
    setIsEditOpen(true);
    setError('');
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const data = {
        title: editingArticle.title,
        content: editingArticle.content,
        summary: editingArticle.summary || editingArticle.content.slice(0, 200),
        source_url: editingArticle.source_url || null,
        source_name: editingArticle.source_name || '',
        tags: editingArticle.tags ? editingArticle.tags.split(',').map(t => t.trim()) : [],
      };
      await newsApi.update(editingArticle.id, data);
      setIsEditOpen(false);
      setEditingArticle(null);
      fetchArticles();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const moveArticle = (index, direction) => {
    const newArticles = [...articles];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newArticles.length) return;
    [newArticles[index], newArticles[targetIndex]] = [newArticles[targetIndex], newArticles[index]];
    setArticles(newArticles);
    setOrderChanged(true);
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      const articleIds = articles.map(a => a.id);
      await newsApi.reorder(articleIds);
      setOrderChanged(false);
    } catch (err) {
      alert(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e, articleId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingFor(articleId);
    try {
      await newsApi.uploadImage(articleId, file);
      fetchArticles();
    } catch (err) {
      alert(formatApiError(err));
    } finally {
      setUploadingFor(null);
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const handleScrape = async () => {
    setScraping(true);
    setScrapeResult(null);
    try {
      const res = await newsApi.scrape();
      setScrapeResult(res.data);
      // Scraper runs in background — refresh the list a few times to surface new content
      [15000, 60000, 180000].forEach((ms) => setTimeout(() => fetchArticles(), ms));
    } catch (err) {
      setScrapeResult({ error: formatApiError(err) });
    } finally {
      setScraping(false);
    }
  };

  const defaultImg = 'https://customer-assets.emergentagent.com/job_crown-vic-central/artifacts/fml311ba_341040780_3390030624586050_8184513587913407132_n.jpg';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FF3B30] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <div className="bg-[#141414] border-b border-[#262626]">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <p className="tag text-[#FF3B30] mb-2">Admin</p>
              <h1 className="font-heading font-bold text-3xl text-white mb-2">Manage News</h1>
              <p className="text-gray-400 text-sm">{articles.length} articles total</p>
            </div>
            <div className="flex items-center gap-3">
              {orderChanged && (
                <Button
                  onClick={saveOrder}
                  disabled={saving}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="save-order-btn"
                >
                  <Save className={`w-4 h-4 mr-2`} />
                  {saving ? 'Saving...' : 'Save Order'}
                </Button>
              )}
              <Button
                onClick={handleScrape}
                disabled={scraping}
                variant="outline"
                className="border-[#262626] text-white"
                data-testid="scrape-now-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${scraping ? 'animate-spin' : ''}`} />
                {scraping ? 'Scraping...' : 'Scrape Now'}
              </Button>

              <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#FF3B30] hover:bg-[#E6352B] text-white" data-testid="add-article-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Article
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-heading text-xl">Add News Article</DialogTitle>
                  </DialogHeader>
                  <p className="text-gray-400 text-sm mb-4">
                    Manually add an article you found on Facebook, social media, or anywhere else.
                  </p>
                  <form onSubmit={handleCreate} className="space-y-4">
                    {error && (
                      <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-3 py-2 rounded text-sm flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        {error}
                      </div>
                    )}
                    <div>
                      <Label>Title *</Label>
                      <Input
                        value={newArticle.title}
                        onChange={(e) => setNewArticle({ ...newArticle, title: e.target.value })}
                        placeholder="Article headline"
                        className="bg-[#1F1F1F] border-[#262626] text-white"
                        required
                        data-testid="article-title-input"
                      />
                    </div>
                    <div>
                      <Label>Content *</Label>
                      <Textarea
                        value={newArticle.content}
                        onChange={(e) => setNewArticle({ ...newArticle, content: e.target.value })}
                        placeholder="Full article text or description"
                        rows={6}
                        className="bg-[#1F1F1F] border-[#262626] text-white"
                        required
                        data-testid="article-content-input"
                      />
                    </div>
                    <div>
                      <Label>Summary (optional - auto-generated from content)</Label>
                      <Input
                        value={newArticle.summary}
                        onChange={(e) => setNewArticle({ ...newArticle, summary: e.target.value })}
                        placeholder="Brief summary"
                        className="bg-[#1F1F1F] border-[#262626] text-white"
                        data-testid="article-summary-input"
                      />
                    </div>
                    <div>
                      <Label>Source URL (Facebook link, website, etc.)</Label>
                      <Input
                        value={newArticle.source_url}
                        onChange={(e) => setNewArticle({ ...newArticle, source_url: e.target.value })}
                        placeholder="https://facebook.com/..."
                        className="bg-[#1F1F1F] border-[#262626] text-white"
                        data-testid="article-source-url-input"
                      />
                    </div>
                    <div>
                      <Label>Source Name</Label>
                      <Input
                        value={newArticle.source_name}
                        onChange={(e) => setNewArticle({ ...newArticle, source_name: e.target.value })}
                        placeholder="e.g., Showtime Speedway Facebook, Crown Vic Nationals"
                        className="bg-[#1F1F1F] border-[#262626] text-white"
                        data-testid="article-source-name-input"
                      />
                    </div>
                    <div>
                      <Label>Tags (comma separated)</Label>
                      <Input
                        value={newArticle.tags}
                        onChange={(e) => setNewArticle({ ...newArticle, tags: e.target.value })}
                        placeholder="crown vic, racing, short track"
                        className="bg-[#1F1F1F] border-[#262626] text-white"
                        data-testid="article-tags-input"
                      />
                    </div>
                    <p className="text-gray-500 text-xs">
                      You can upload an image after creating the article.
                    </p>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-[#FF3B30] hover:bg-[#E6352B]"
                      data-testid="submit-article-btn"
                    >
                      {submitting ? 'Creating...' : 'Create Article'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Scrape Result */}
          {scrapeResult && (
            <div className="mt-4 p-4 bg-[#1F1F1F] rounded border border-[#262626]">
              {scrapeResult.error ? (
                <p className="text-red-400 text-sm" data-testid="scrape-error">{scrapeResult.error}</p>
              ) : scrapeResult.ok ? (
                <div className="text-sm text-gray-300" data-testid="scrape-running">
                  <p className="font-medium text-white mb-1">Scrape running in background</p>
                  <p>{scrapeResult.message || 'New articles will appear in the list shortly. Auto-refreshing in 15s, 1m, and 3m.'}</p>
                </div>
              ) : (
                <div className="text-sm text-gray-300">
                  <p className="font-medium text-white mb-1">Scrape Complete</p>
                  <p>Found {scrapeResult.total_found} articles, {scrapeResult.new_saved} new saved, {scrapeResult.images_stored || 0} images stored</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Articles List */}
      <div className="container mx-auto px-4 py-8">
        <input
          type="file"
          ref={fileInputRef}
          onChange={(e) => {
            if (uploadingFor) handleImageUpload(e, uploadingFor);
          }}
          accept="image/*"
          className="hidden"
        />

        <div className="space-y-3">
          {articles.map((article, index) => (
            <Card key={article.id} className="bg-[#141414] border-[#262626] p-4" data-testid={`admin-article-${article.id}`}>
              <div className="flex gap-4">
                {/* Reorder Arrows */}
                <div className="flex flex-col justify-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => moveArticle(index, -1)}
                    disabled={index === 0}
                    className={`w-7 h-7 rounded flex items-center justify-center transition-colors ${
                      index === 0 ? 'text-gray-700' : 'text-gray-400 hover:text-white hover:bg-[#262626]'
                    }`}
                    data-testid={`move-up-${article.id}`}
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <span className="text-[10px] text-gray-600 text-center font-mono">{index + 1}</span>
                  <button
                    onClick={() => moveArticle(index, 1)}
                    disabled={index === articles.length - 1}
                    className={`w-7 h-7 rounded flex items-center justify-center transition-colors ${
                      index === articles.length - 1 ? 'text-gray-700' : 'text-gray-400 hover:text-white hover:bg-[#262626]'
                    }`}
                    data-testid={`move-down-${article.id}`}
                  >
                    <ArrowDown className="w-4 h-4" />
                  </button>
                </div>

                {/* Thumbnail */}
                <div className="w-24 h-20 flex-shrink-0 rounded overflow-hidden bg-[#262626] relative group">
                  <img
                    src={resolveImageUrl(article.image_url) || defaultImg}
                    alt={article.title}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = defaultImg; }}
                  />
                  <button
                    onClick={() => {
                      setUploadingFor(article.id);
                      fileInputRef.current?.click();
                    }}
                    className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                    data-testid={`upload-img-${article.id}`}
                  >
                    {uploadingFor === article.id ? (
                      <RefreshCw className="w-5 h-5 text-white animate-spin" />
                    ) : (
                      <Upload className="w-5 h-5 text-white" />
                    )}
                  </button>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-white text-sm truncate mb-1">{article.title}</h3>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDate(article.created_at)}
                    </span>
                    {article.source_name && (
                      <span className="text-gray-400">{article.source_name}</span>
                    )}
                    {article.author_name && (
                      <span className={`tag px-1.5 py-0.5 rounded ${
                        article.author_name.includes('Auto-Scraped') 
                          ? 'bg-blue-500/20 text-blue-400' 
                          : 'bg-green-500/20 text-green-400'
                      }`}>
                        {article.author_name.includes('Auto-Scraped') ? 'SCRAPED' : 'MANUAL'}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {article.source_url && (
                    <a href={article.source_url} target="_blank" rel="noopener noreferrer">
                      <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </a>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openEdit(article)}
                    className="text-gray-400 hover:text-white"
                    data-testid={`edit-article-${article.id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(article.id)}
                    className="text-red-400 hover:text-red-300"
                    data-testid={`delete-article-${article.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {articles.length === 0 && (
          <div className="text-center py-16">
            <Image className="w-16 h-16 mx-auto text-gray-600 mb-4" />
            <p className="text-gray-400 text-lg">No articles yet.</p>
          </div>
        )}
      </div>

      {/* Edit Article Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Edit Article</DialogTitle>
          </DialogHeader>
          {editingArticle && (
            <form onSubmit={handleEdit} className="space-y-4">
              {error && (
                <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-3 py-2 rounded text-sm flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
              <div>
                <Label>Title *</Label>
                <Input
                  value={editingArticle.title}
                  onChange={(e) => setEditingArticle({ ...editingArticle, title: e.target.value })}
                  className="bg-[#1F1F1F] border-[#262626] text-white"
                  required
                  data-testid="edit-article-title"
                />
              </div>
              <div>
                <Label>Content *</Label>
                <Textarea
                  value={editingArticle.content}
                  onChange={(e) => setEditingArticle({ ...editingArticle, content: e.target.value })}
                  rows={6}
                  className="bg-[#1F1F1F] border-[#262626] text-white"
                  required
                  data-testid="edit-article-content"
                />
              </div>
              <div>
                <Label>Summary</Label>
                <Input
                  value={editingArticle.summary}
                  onChange={(e) => setEditingArticle({ ...editingArticle, summary: e.target.value })}
                  className="bg-[#1F1F1F] border-[#262626] text-white"
                  data-testid="edit-article-summary"
                />
              </div>
              <div>
                <Label>Source URL</Label>
                <Input
                  value={editingArticle.source_url}
                  onChange={(e) => setEditingArticle({ ...editingArticle, source_url: e.target.value })}
                  className="bg-[#1F1F1F] border-[#262626] text-white"
                  data-testid="edit-article-source-url"
                />
              </div>
              <div>
                <Label>Source Name</Label>
                <Input
                  value={editingArticle.source_name}
                  onChange={(e) => setEditingArticle({ ...editingArticle, source_name: e.target.value })}
                  className="bg-[#1F1F1F] border-[#262626] text-white"
                  data-testid="edit-article-source-name"
                />
              </div>
              <div>
                <Label>Tags (comma separated)</Label>
                <Input
                  value={editingArticle.tags}
                  onChange={(e) => setEditingArticle({ ...editingArticle, tags: e.target.value })}
                  className="bg-[#1F1F1F] border-[#262626] text-white"
                  data-testid="edit-article-tags"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#FF3B30] hover:bg-[#E6352B]"
                data-testid="save-article-btn"
              >
                {submitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
